import classNames from 'classnames';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RewardType } from '@loyalty/shared-common/rewards-api-client';
import { Suspense, useEffect, useState } from 'react';
import { defaultCurrencyImage, getLocalizedProperty } from '@loyalty/shared-common';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { EmptyState } from './subcomponents/EmptyState';
import {
  ProfileRewardFrontendAttributes,
  RewardsWonTableBackendProps,
  RewardsWonTableFrontendProps,
} from './helpers/types';
import { ReactBootstrap } from '../../ReactBootstrap';
import { RewardCode } from './subcomponents/RewardCode';
import { RewardLabel } from './subcomponents/RewardLabel';
import { RewardsWonTableSkeleton } from './subcomponents/RewardsWonTableSkeleton';
import { TranslationProvider } from '../TranslationProvider/TranslationProvider';
import { byDateDesc } from './helpers/by-date-desc';
import { getCampaignRewardToken } from './helpers/get-campaign-reward-token';

const PAGE_SIZE = 10;
const CLASSNAME = 'qlfeditor-rewards-won__table';

export function serializeRewardsWonTableComponentProps({
  state,
  options,
  context,
}: RewardsWonTableBackendProps): RewardsWonTableFrontendProps {
  const { locale } = context.i18n;

  const {
    profile: { rewards: profileRewards = [] },
    currencies,
  } = state;

  const selectedCurrencyIds = options.attributes.currencyIds;
  // retro-compatibility: undefined means include free rewards
  const includeFreeRewards = options.attributes.includeFreeRewards !== false;

  const filteredProfileRewards = profileRewards.filter(profileReward => {
    const isFreeReward = !profileReward.currencyId || !profileReward.amount;
    if (isFreeReward) {
      return includeFreeRewards;
    }

    // retro-compatibility: show all currencies by default
    if (!selectedCurrencyIds) {
      return true;
    }

    return selectedCurrencyIds.includes(profileReward.currencyId!);
  });

  const formatedProfileRewards = filteredProfileRewards.map(profileReward => {
    const currencyId = profileReward.currencyId;
    const currency = currencies.find(c => c.id === currencyId);
    let token: string | undefined = undefined;
    let tokenPayload: Record<string, any> | undefined = undefined;
    let tokenExpirationDate: string | undefined = undefined;
    if (profileReward.type === RewardType.CAMPAIGN_ACCESS) {
      const tokenInfo = getCampaignRewardToken(profileReward, locale);
      token = tokenInfo.token;
      tokenPayload = tokenInfo.tokenPayload;
      tokenExpirationDate = tokenPayload?.exp
        ? new Date(tokenPayload.exp * 1000).toISOString()
        : undefined;
    }

    const channelInfo = profileReward.data?.channelInfoByLanguage?.[locale];
    return {
      image: getLocalizedProperty(profileReward, 'image', locale) || '',
      name: getLocalizedProperty(profileReward, 'name') || '',
      description: getLocalizedProperty(profileReward, 'description') || '',
      details: getLocalizedProperty(profileReward, 'details') || '',
      detailsLabel: getLocalizedProperty(profileReward, 'detailsLabel', locale),
      cost: profileReward.amount || 0,
      code:
        (profileReward.type === RewardType.CAMPAIGN_ACCESS ? token : profileReward.promocode) || '',
      channelInfo,
      currencyName: getLocalizedProperty(currency, 'name', locale),
      currencyImage: getLocalizedProperty(currency, 'image', locale),
      createdAt: profileReward.createdAt!,
      type: profileReward.type,
      expirationDate: tokenExpirationDate,
    };
  });

  return {
    id: options.id,
    attributes: options.attributes,
    rewards: formatedProfileRewards,
    locale,
  };
}

export function RewardsWonTableComponent(props: RewardsWonTableFrontendProps) {
  const queryClient = new QueryClient();

  return (
    <Suspense fallback={<RewardsWonTableSkeleton />}>
      <TranslationProvider loader={<RewardsWonTableSkeleton />}>
        <QueryClientProvider client={queryClient}>
          <Component {...props} />
        </QueryClientProvider>
      </TranslationProvider>
    </Suspense>
  );
}

function Component(minProps: RewardsWonTableFrontendProps) {
  const { id, attributes, rewards, locale } = minProps;
  const { class: className, campaignFeedApiHost } = attributes;
  const { t } = useTranslation('participant');
  const [pageNumber, setPageNumber] = useState(0);
  const [rewardsPage, setRewardsPage] = useState<ProfileRewardFrontendAttributes[]>([]);
  const pageCount = Math.ceil(rewards.length / PAGE_SIZE);
  const sortedRewards = rewards.sort(byDateDesc);

  useEffect(() => {
    setRewardsPage(sortedRewards.slice(pageNumber * PAGE_SIZE, (pageNumber + 1) * PAGE_SIZE));
  }, [pageNumber]);

  if (!rewards.length) {
    return (
      <div className={classNames(CLASSNAME, className)}>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className={classNames(CLASSNAME, className)}>
      <table className={classNames(CLASSNAME, 'qlf-table')}>
        <caption className="sr-only">{t('aria.rewards-won.table.caption')}</caption>
        <thead>
          <tr>
            <th className="label">
              <i className="fas fa-gift"></i>
              <span style={{ marginLeft: '5px' }}>
                {t('editor.rewards-won.table.columns.label')}
              </span>
            </th>

            <th className="code">
              <span>{t('editor.rewards-won.table.columns.code')}</span>
            </th>
            <th className="points">
              <i
                className="fas fa-usd-circle"
                aria-label={t('aria.rewards-won.table.columns.points')}
              ></i>
            </th>
          </tr>
        </thead>
        <tbody>
          {rewardsPage.map((profileReward, index) => {
            return (
              <tr key={`${id}_rewards_page_${index}`}>
                <td className="label">
                  <RewardLabel reward={profileReward} />
                </td>
                <td className="code">
                  <RewardCode
                    profileReward={profileReward}
                    campaignFeedApiHost={campaignFeedApiHost}
                    locale={locale}
                  />
                </td>
                <td className="points">
                  <span style={{ marginRight: '5px' }}>
                    {Math.round(profileReward.cost) || t('editor.rewards-won.table.amount.free')}
                  </span>
                  <img
                    src={profileReward.currencyImage || defaultCurrencyImage}
                    width="20px"
                    height="20px"
                    alt={
                      profileReward.currencyName ||
                      t('editor.rewards-won.table.default.currency-name')
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
        {rewards.length > PAGE_SIZE && (
          <tfoot>
            <tr>
              <td colSpan={3}>
                <div className="pagination qlfeditor-pagination--section__cta">
                  <button
                    id={`prev-page-{id}`}
                    className="qlfeditor-pagination--button__cta"
                    onClick={() => setPageNumber(prev => prev - 1)}
                    disabled={pageNumber === 0}
                    aria-label={t('aria.previousPage')}
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                  <div className="qlfeditor--pagination__cta">
                    {t('widgets.rewards-won.pagination.position', {
                      current: (pageNumber + 1).toFixed(0),
                      total: pageCount == 0 ? 1 : pageCount,
                    })}
                  </div>
                  <button
                    id={`next-page-{id}`}
                    className="qlfeditor-pagination--button__cta"
                    onClick={() => setPageNumber(prev => prev + 1)}
                    disabled={pageNumber + 1 === pageCount}
                    aria-label={t('aria.nextPage')}
                  >
                    <i className="fa fa-chevron-right"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export function RewardsWonTableRenderer(props: RewardsWonTableBackendProps, document?: Document) {
  const reactProps = serializeRewardsWonTableComponentProps(props);
  return renderToString(
    <ReactBootstrap
      component="RewardsWonTableComponent"
      props={reactProps}
      document={document}
      componentType="rewards-won-table"
    />,
  );
}
